import { useEffect } from "react";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchApiGet, fetchApiPost } from "@/lib/api/utils";
import type {
  BulkPairContractChangesRequest,
  ContractMatchProposalsQuery,
  ListContractChangesQuery,
  PairContractChangeItem,
} from "@/lib/validation/contractMatching";
import type {
  BulkPairContractChangesResult,
  ContractMatchProposalsResult,
  ListContractChangesResult,
  PairContractChangeResult,
} from "./types";

export type {
  AgtechContractMatchCandidate,
  BulkPairContractChangesResult,
  ContractChangeDto,
  ContractMatchCriterion,
  ContractMatchCriterionResult,
  ContractMatchProposalResult,
  ContractMatchProposalsResult,
  ContractMatchTargetCustomer,
  ContractMatchTargetProjection,
  ContractMatchTargetType,
  FailedBulkPairContractChangeItem,
  ListContractChangesResult,
  PairContractChangeResult,
  StaleContractChange,
} from "./types";

export const contractMatchingBaseKey = "contractMatching";
export const contractChangeLoadLimit = 400;
const contractChangePageSize = 100;
const contractChangePageLimit = contractChangeLoadLimit / contractChangePageSize;

export function useContractMatchProposals(query: ContractMatchProposalsQuery) {
  const queryParams = new URLSearchParams({
    page: query.page.toString(),
    page_size: query.page_size.toString(),
    target_type: query.target_type,
  });

  return useQuery({
    queryKey: [contractMatchingBaseKey, "proposals", queryParams.toString()],
    queryFn: () => fetchApiGet<ContractMatchProposalsResult>("contracts/matching/proposals", queryParams),
    keepPreviousData: true,
    retry: 1,
  });
}

export function useListContractChanges(enabled: boolean) {
  const result = useInfiniteQuery({
    queryKey: [contractMatchingBaseKey, "list", "unmatched", "active", 1],
    queryFn: ({ pageParam = 1 }) => {
      const query: ListContractChangesQuery = {
        page: pageParam,
        page_size: contractChangePageSize,
        match_status: "unmatched",
        archived: "false",
        schema_version: 1,
      };
      const queryParams = new URLSearchParams({
        page: query.page.toString(),
        page_size: query.page_size.toString(),
        match_status: query.match_status,
        archived: query.archived,
        schema_version: query.schema_version.toString(),
      });
      return fetchApiGet<ListContractChangesResult>("contracts/matching/list", queryParams);
    },
    getNextPageParam: (lastPage, pages) => {
      if (pages.length >= contractChangePageLimit) return undefined;
      return lastPage.success && lastPage.data.has_next_page ? lastPage.data.page + 1 : undefined;
    },
    enabled,
    retry: 1,
  });

  const pages = result.data?.pages ?? [];
  const lastPage = pages[pages.length - 1];
  const reachedLimitWithMoreAvailable = pages.length >= contractChangePageLimit
    && lastPage?.success
    && lastPage.data.has_next_page;

  useEffect((): void => {
    if (
      enabled
      && pages.length > 0
      && result.hasNextPage
      && !result.isFetchingNextPage
    ) {
      void result.fetchNextPage();
    }
  }, [enabled, pages.length, result.hasNextPage, result.isFetchingNextPage, result.fetchNextPage]);

  return { ...result, reachedLimitWithMoreAvailable };
}

export function usePairContractChange() {
  const queryClient = useQueryClient();

  return async (data: PairContractChangeItem) => {
    const result = await fetchApiPost<PairContractChangeResult>("contracts/matching/pair", data);
    if (result.success) {
      await queryClient.invalidateQueries({ queryKey: [contractMatchingBaseKey] });
    }
    return result;
  };
}

export function useBulkPairContractChanges() {
  const queryClient = useQueryClient();

  return async (data: BulkPairContractChangesRequest) => {
    const result = await fetchApiPost<BulkPairContractChangesResult>("contracts/matching/bulk-pair", data);
    if (result.success) {
      await queryClient.invalidateQueries({ queryKey: [contractMatchingBaseKey] });
    }
    return result;
  };
}
